import { createPortal } from 'react-dom';
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { SubmitButton } from './Buttons';
import styles from './LowTokensNotification.module.css';

interface LowTokensNotificationProps {
  tokens: number;
  onClose: () => void;
  isFreeUser?: boolean;
  maxTokens?: number;
}

const LowTokensNotification = ({ tokens, onClose, isFreeUser = false, maxTokens }: LowTokensNotificationProps) => {
  const { t } = useTranslation('common');
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();

  const threshold = isFreeUser ? 20 : 200;
  const total = maxTokens ?? threshold;
  const percentage = total > 0 ? Math.max(0, Math.min((tokens / total) * 100, 100)) : 0;
  const fillState = percentage <= 20 ? 'danger' : percentage <= 40 ? 'warning' : '';

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleClose = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.impact('light');
    onClose();
  }, [haptic, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) handleClose(e);
  }, [handleClose]);

  const handleTopUp = useCallback(() => {
    haptic.impact('medium');
    onClose();
    navigate(isFreeUser ? '/pricing' : '/account?screen=usage');
  }, [haptic, onClose, navigate, isFreeUser]);

  return createPortal(
    <div className={styles.backdrop} onClick={handleBackdropClick} onTouchEnd={handleBackdropClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {isFreeUser ? t('tokens.lowBalance.free') : t('tokens.lowBalance.paid')}
          </h2>
          <button className={styles.closeButton} onClick={handleClose} onTouchEnd={handleClose} title={t('btn.close')}>
            ×
          </button>
        </div>

        <p className={styles.message}>
          {isFreeUser ? t('tokens.lowBalance.freeDescription') : t('tokens.lowBalance.left')}
        </p>

        <div className={styles.progressRow}>
          <div className={styles.progressTrack}>
            <div
              className={`${styles.progressFill} ${fillState ? styles[fillState] : ''}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className={styles.progressLabel}>
            {tokens.toLocaleString()} / {total.toLocaleString()}
          </span>
        </div>

        <div className={styles.footer}>
          <SubmitButton
            label={isFreeUser ? t('btn.buySubscription') : t('btn.topUp')}
            onClick={handleTopUp}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LowTokensNotification;
